import React from "react"
import Img from "gatsby-image"
import {
  ContentfulRichTextGatsbyReference,
  renderRichText,
  RenderRichTextData,
} from "gatsby-source-contentful/rich-text"
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types"
import { Options } from "@contentful/rich-text-react-renderer"

import { Bold, Text, Heading1 } from "../markdown/author"
import { useContentfulImage } from "../hooks/useContentfulImage"

type BlogBodyProps = {
  content: RenderRichTextData<ContentfulRichTextGatsbyReference>
}

export const BlogBody = ({ content }: BlogBodyProps) => {
  const options: Options = {
    renderMark: {
      [MARKS.BOLD]: text => <Bold>{text}</Bold>,
      [MARKS.ITALIC]: text => <i>{text}</i>,
      [MARKS.UNDERLINE]: text => <u>{text}</u>,
      [MARKS.CODE]: text => <div style={{}}>{text}</div>,
    },
    renderNode: {
      [BLOCKS.HEADING_1]: (node, children) => <Heading1>{children}</Heading1>,
      [BLOCKS.HEADING_2]: (node, children) => <h2>{children}</h2>,
      [BLOCKS.HEADING_3]: (node, children) => <h3>{children}</h3>,
      [BLOCKS.HEADING_4]: (node, children) => <h4>{children}</h4>,
      [BLOCKS.HEADING_5]: (node, children) => <h5>{children}</h5>,
      [BLOCKS.HEADING_6]: (node, children) => <h6>{children}</h6>,
      [BLOCKS.PARAGRAPH]: (node, children) => <Text>{children}</Text>,
      [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
        const { bodyContent, backgroundColor, textColor } = node.data.target

        return (
          <div
            className="extraColor"
            style={{
              backgroundColor: `${node.data.target.backgroundColour}`,
              color: `${node.data.target.textColour}`,
              width: `100% !important`,
            }}
          >
            <article className="blog-body">
              <Text>{children}</Text>
            </article>
          </div>
        )
      },
      [BLOCKS.UL_LIST]: (node, children) => <ul>{children}</ul>,
      [BLOCKS.OL_LIST]: (node, children) => <ol>{children}</ol>,
      [BLOCKS.LIST_ITEM]: (node, children) => <li>{children}</li>,
      [BLOCKS.QUOTE]: (node, children) => <blockquote>{children}</blockquote>,
      [BLOCKS.HR]: () => <hr />,
      [BLOCKS.EMBEDDED_ASSET]: node => {
        const {
          fluid: { src },
          title,
        } = node.data.target

        return <img src={src} alt={title} />
      },
      [INLINES.HYPERLINK]: (node, children) => {
        if (node.data.uri.indexOf("youtube.com") !== -1) {
          return (
            <iframe
              src={node.data.uri}
              frameborder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              frameBorder="0"
              webkitallowfullscreen="true"
              mozallowfullscreen="true"
              allowFullScreen
            ></iframe>
          )
        } else if (node.data.uri.indexOf("player.vimeo.com") !== -1) {
          return (
            <iframe
              src={node.data.uri}
              frameborder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              frameBorder="0"
              webkitallowfullscreen="true"
              mozallowfullscreen="true"
              allowFullScreen
            ></iframe>
          )
        } else if (node.data.uri.indexOf("soundcloud.com") !== -1) {
          return (
            <iframe
              width="100%"
              height="300"
              scrolling="no"
              frameborder="no"
              allow="autoplay"
              src={node.data.uri}
            ></iframe>
          )
        } else {
          return (
            <a
              href={node.data.uri}
              style={{ mixBlendMode: "exclusion", color: "#fff" }}
              target="_blank"
            >
              {children}
            </a>
          )
        }
      },
    },
  }

  return (
    <article className="blog-body">{renderRichText(content, options)}</article>
  )
}
